import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { LayoutDashboard, UserCheck, CreditCard, Mail, MessageSquare } from "lucide-react";
import { getTenantInfo } from "../../utils/domain";

export default function TenantFloatingDock() {
  const location = useLocation();
  const reduxUser = useSelector((state) => state.user);
  const tenantInfo = getTenantInfo();

  const pathname = location.pathname;

  // Do not show on customer storefront domains
  if (tenantInfo?.isTenantFrontend) return null;

  // Do not show on auth pages
  const isAuthPage =
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/forgotpassword" ||
    pathname === "/reset-password" ||
    pathname === "/changepassword" ||
    pathname === "/create-password";

  if (isAuthPage) return null;

  const isTenantPage =
    pathname.startsWith("/tenant") ||
    pathname === "/tenant-dashboard" ||
    pathname === "/dashboard";

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const isTenantUser =
    reduxUser?.role === "tenant" ||
    reduxUser?.role === "Tenant-Admin" ||
    Boolean(reduxUser?.tenant_id) ||
    Boolean(reduxUser?.isTenant);

  // Show whenever user is on tenant routes or logged in as tenant
  if (!isTenantPage && !(token && isTenantUser)) {
    return null;
  }

  // Tenant Modules in Floating Dock
  const primaryModules = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/tenant/dashboard",
      active: pathname === "/tenant/dashboard" || pathname === "/tenant-dashboard",
    },
    {
      id: "customers",
      label: "Customers",
      icon: UserCheck,
      path: "/tenant/customers",
      active:
        pathname.startsWith("/tenant/customers") ||
        pathname.startsWith("/tenant/team"),
    },
    {
      id: "inquiries",
      label: "Inquiries",
      icon: MessageSquare,
      path: "/tenant/inquiries",
      active:
        pathname.startsWith("/tenant/inquiries") ||
        pathname.startsWith("/tenant/contact-us"),
    },
    {
      id: "email-templates",
      label: "Email Templates",
      icon: Mail,
      path: "/tenant/email-templates",
      active: pathname.startsWith("/tenant/email-templates"),
    },
    {
      id: "plans",
      label: "Subscription",
      icon: CreditCard,
      path: "/tenant/plans",
      active:
        pathname.startsWith("/tenant/plans") ||
        pathname.startsWith("/tenant/subscription"),
    },
  ];

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-[95vw]">
      <nav
        aria-label="Tenant Quick Navigation Dock"
        className="relative bg-white/80 backdrop-blur-2xl border border-white/85 ring-1 ring-slate-900/10 shadow-[0_20px_50px_-10px_rgba(15,23,42,0.22),inset_0_1px_1px_rgba(255,255,255,0.9),inset_0_-1px_1px_rgba(0,0,0,0.05)] rounded-2xl p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 overflow-x-auto max-w-full scrollbar-none"
      >
        {primaryModules.map((item) => {
          const Icon = item.icon;
          const isActive = item.active;

          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 shrink-0 select-none ${
                isActive
                  ? "bg-[#115B9D]/95 text-white shadow-[0_4px_16px_-2px_rgba(17,91,157,0.45)] ring-1 ring-white/30"
                  : "text-slate-700 hover:text-slate-900 hover:bg-white/90 hover:shadow-xs active:scale-95"
              }`}
            >
              <Icon
                size={16}
                className={`shrink-0 transition-transform ${
                  isActive ? "text-white scale-105" : "text-slate-500"
                }`}
              />
              <span className="whitespace-nowrap tracking-tight">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
